import { Button, TextField, Tooltip } from "@material-ui/core";
import { IconButton } from "@material-ui/core";
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import NotificationProvider from "components/NotificationProvider";
import ShimmerLoader from "components/ShimmerLoader/ShimmerLoader";
import RichTextEditor from "components/RichTextEditor/RichTextEditor";
import DOMPurify from "dompurify";
import { useState, useEffect } from "react";
import { useGetVendorDetachedTemplateQuery, useCreateVendorDetachedTemplateMutation, useUpdateVendorDetachedTemplateMutation } from "./vendorDetachedTemplateSlice";
import { vendorDetachedTemplateStyle } from "./VendorDetachedTemplateStyles";

export const VendorDetachedTemplate = ({ vendorId }) => {
    const classes = vendorDetachedTemplateStyle();
    const [isEditing, setIsEditing] = useState(false);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');

    const { data: template, isLoading } = useGetVendorDetachedTemplateQuery(vendorId, { skip: !vendorId });
    const [createTemplate, { isLoading: isCreating }] = useCreateVendorDetachedTemplateMutation();
    const [updateTemplate, { isLoading: isUpdating }] = useUpdateVendorDetachedTemplateMutation();
    const isSaving = isCreating || isUpdating;

    useEffect(() => {
        if (template) {
            setSubject(template.subject || '');
            setBody(template.body || '');
        }
    }, [template]);

    const handleEdit = () => {
        setSubject(template?.subject || '');
        setBody(template?.body || '');
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    const handleSave = async () => {
        const isVendorSpecific = template?.vendorId != null;
        const mutation = isVendorSpecific ? updateTemplate : createTemplate;
        const res = await mutation({ vendorId, subject, body });
        if (!res?.error) {
            NotificationProvider.success('Successfully updated detached template');
            setIsEditing(false);
        } else {
            NotificationProvider.error('Failed to update detached template');
        }
    };

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <div className={classes.sectionTitle} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Detached Template
                    <Tooltip title="This is the email template sent to the vendor when a work order is detached from them.">
                        <InfoOutlinedIcon style={{ fontSize: 16, color: '#757575', cursor: 'default' }} />
                    </Tooltip>
                </div>
                {!isEditing && (
                    <Authorize profile={permissionProfiles.VENDOR.EDIT_DETACHED_TEMPLATE}>
                        <IconButton size="small" onClick={handleEdit}>
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Authorize>
                )}
            </div>

            {isLoading ? (
                <ShimmerLoader count={2} height="80" width={12} />
            ) : isEditing ? (
                <>
                    <div className={classes.fieldLabel}>Subject</div>
                    <TextField
                        className={classes.subjectInput}
                        variant="outlined"
                        size="small"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                    />
                    <div className={classes.fieldLabel}>Body</div>
                    <div className={classes.bodyWrapper}>
                        <RichTextEditor
                            content={body}
                            onChange={setBody}
                        />
                    </div>
                    <div className={classes.actions}>
                        <Button onClick={handleCancel} color="primary">Cancel</Button>
                        <Button
                            onClick={handleSave}
                            color="primary"
                            variant="contained"
                            disabled={isSaving}>
                            Save
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <div className={classes.fieldLabel}>Subject</div>
                    <div className={classes.fieldValue}>{template?.subject || '—'}</div>
                    <div className={classes.fieldLabel}>Body</div>
                    <div
                        className={classes.fieldValue}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template?.body || '') }}
                    />
                </>
            )}
        </div>
    );
};
